import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Header, Icon, Table } from "semantic-ui-react";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { addHistoryToDb } from "../../../app/firestore/firestoreService";
import {
  convertFileTypeToIcon,
  docIsTypeImage,
  docIsTypeLink,
  docIsTypePdf,
  downloadNonPdf,
  downloadPdf,
} from "../../../app/common/util/util";
import { openModal } from "../../../app/common/modals/modalSlice";

// One row per not-yet-viewed document (not per transaction, like the other
// dashboard drill-downs) - each row lets the user View, Download, or jump to
// the full transaction, same as the per-transaction documents page.
export default function PartyNewDocsTable({ items, currentUserProfile }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  if (!items || items.length === 0) return null;

  return (
    <div className="medium bottom margin">
      <Header size="medium" color="blue" className="small bottom margin">
        New Docs
      </Header>
      <Table compact className="mini top margin">
        <Table.Header className="mobile hidden">
          <Table.Row className="small-header">
            <Table.HeaderCell></Table.HeaderCell>
            <Table.HeaderCell>Document</Table.HeaderCell>
            <Table.HeaderCell>Address</Table.HeaderCell>
            <Table.HeaderCell>Shared</Table.HeaderCell>
            <Table.HeaderCell></Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {items.map((item) => (
            <PartyNewDocsTableRow
              key={`${item.party.transactionId}-${item.doc.id}`}
              item={item}
              currentUserProfile={currentUserProfile}
              dispatch={dispatch}
              navigate={navigate}
            />
          ))}
        </Table.Body>
      </Table>
    </div>
  );
}

function PartyNewDocsTableRow({ item, currentUserProfile, dispatch, navigate }) {
  const { doc, party, transaction, sharedAt } = item;
  const docIsLink = docIsTypeLink(doc);

  function handleView() {
    addHistoryToDb(doc.transactionId, party, "viewed", doc.name);
    if (docIsLink) {
      if (doc.linkUrl) {
        window.open(doc.linkUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error("No link found for this document.");
      }
    } else if (docIsTypeImage(doc)) {
      dispatch(openModal({ modalType: "DocViewImageFileModal", modalProps: { doc } }));
    } else {
      navigate(`/party/${party.id}/documents/${doc.id}/view`);
    }
  }

  async function handleDownload() {
    addHistoryToDb(doc.transactionId, party, "downloaded", doc.name);
    try {
      if (docIsTypePdf(doc)) {
        await downloadPdf(doc, transaction, currentUserProfile);
      } else if (doc.filetypeMIME) {
        await downloadNonPdf(doc);
      } else {
        toast.error("Error with downloading document.");
      }
    } catch (error) {
      toast.error(error.message);
    }
  }

  function handleGoToTransaction() {
    navigate(`/party/${party.id}/documents`);
  }

  return (
    <Table.Row>
      <Table.Cell collapsing>
        <Icon
          name={docIsLink ? "linkify" : `file ${convertFileTypeToIcon(doc)} outline`}
          size="large"
        />
      </Table.Cell>
      <Table.Cell>{doc.name}</Table.Cell>
      <Table.Cell>{party.address?.street}</Table.Cell>
      <Table.Cell>{sharedAt ? format(sharedAt, "MM/dd/yyyy") : ""}</Table.Cell>
      <Table.Cell>
        {!docIsLink && (
          <Button size="small" onClick={handleDownload}>
            Download
          </Button>
        )}
        <Button size="small" onClick={handleView}>
          View
        </Button>
        <Button primary size="small" onClick={handleGoToTransaction}>
          Go to Transaction
        </Button>
      </Table.Cell>
    </Table.Row>
  );
}
